import styled, { css, keyframes } from 'styled-components';
import { defaultTheme } from '@styles/theme.styled';
import Image from '@components/image';
import Grid from '@components/grid';
import { generateScales } from '@helpers/generate-media-queries';
import Section from '@components/section';
import Text from '@components/text';

export const StyledText = styled(Text)`
  ${({ theme }) => css`
    @media only screen and (max-width: ${theme.breakpoints.sm}) {
      font-size: ${theme.fontSize.heading4.mobile};
    }
  `}
`;

export const StyledSection = styled(Section)`
  ${({ theme }) => css`
    padding-bottom: 0;

    &::after {
      content: '';
      display: block;
      height: 50%;
      background-color: ${theme.colors.light};
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      z-index: 0;
    }
  `}
`;

export const SecurityGrid = styled(Grid)`
  ${({ theme }) => css`
    column-gap: 5rem;
    row-gap: 3rem;
    justify-content: center;
    display: flex;
    flex-wrap: wrap;

    @media only screen and (max-width: ${theme.breakpoints.md}) {
      row-gap: 3rem;
      column-gap: 6rem;
    }

    @media only screen and (max-width: ${theme.breakpoints.sm}) {
      row-gap: 4rem;
      column-gap: 4rem;
    }
  `}
`;

interface ContainerProps {
  backgroundColor: keyof typeof defaultTheme.backgroundColors;
}

export const Enclosure = styled.div<ContainerProps>`
  ${({ theme, backgroundColor }) => css`
    display: flex;
    flex-direction: column;
    gap: 2.5rem;
    background-color: ${theme.backgroundColors[backgroundColor]};
    border-radius: ${theme.borderRadius.large};
    padding: 6rem 3rem;
    overflow: hidden;

    @media only screen and (max-width: ${theme.breakpoints.lg}) {
      padding: ${theme.padding.cards.md};
      border-radius: 1.5rem;
    }

    @media only screen and (max-width: ${theme.breakpoints.md}) {
      padding: 4rem 2rem;
      border-radius: 1rem;
    }

    @media only screen and (max-width: ${theme.breakpoints.sm}) {
      padding: 3rem 1rem;
    }
  `}
`;

export const Container = styled.div<ContainerProps>`
  ${({ backgroundColor = 'white' }) => css`
    display: flex;
    flex-direction: column;
    row-gap: 1.5rem;
    width: 100%;
    max-width: 56rem;
    margin: 0 auto;
    text-align: center;

    ${(backgroundColor === 'dark' || backgroundColor === 'primary') &&
    css`
      color: white;
    `};
  `}
`;

interface SecurtiyImageProps {
  width?: number;
  height?: number;
}

export const SecurtiyImage = styled(Image)<SecurtiyImageProps>`
  ${({ theme, width, height }) => css`
    max-height: 4rem;
    max-width: 12rem;
    align-self: center;
    margin: 0;
    aspect-ratio: ${width} / ${height};

    @media only screen and (max-width: ${theme.breakpoints.lg}) {
      max-height: 4.5rem;
      max-width: 9rem;
    }
  `}
`;

interface TickerProps {
  itemCount?: number;
}

const tickerScroll = keyframes`
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-50%);
  }
`;

export const Ticker = styled.div<TickerProps>`
  ${({ theme, itemCount = 1 }) => css`
    display: flex;
    flex-direction: row;
    ${generateScales('column-gap', '2.5rem', '7.5rem', theme)}
    ${generateScales('height', '2.5rem', '7.5rem', theme)}
    width: fit-content;
    margin: 0 auto;
    text-align: center;
    animation: ${tickerScroll} ${itemCount * 5}s linear infinite;

    &:hover {
      animation-play-state: paused;
    }

    ${SecurtiyImage} {
      max-height: 4.5rem;
      max-width: 9rem;
      height: 100%;
      transition: transform 0.125s ease-in-out;
      transition-delay: 0.125s;
      transition-duration: 0.25s;

      &:hover {
        transform: scale(1.25);
        transition-delay: 0s;
        transition-duration: 0.125s;
      }
    }

    @media only screen and (max-width: ${theme.breakpoints.lg}) {
      ${SecurtiyImage} {
        max-height: 3.75rem;
        max-width: 7.5rem;
      }
    }

    @media only screen and (max-width: ${theme.breakpoints.md}) {
      ${SecurtiyImage} {
        max-height: 3.5rem;
        max-width: 7rem;
      }
    }

    @media only screen and (max-width: ${theme.breakpoints.sm}) {
      text-align: left;

      ${SecurtiyImage} {
        max-height: 3rem;
        max-width: 6rem;
      }
    }
  `}
`;
